import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Card } from 'react-bootstrap';
import useDocTypeRules from '../../../../../hooks/useDocTypeRules';
import { DialogContext } from '../../../../../store/context/DialogContext';
import { DOC_TYPE_FORM_TYPES } from '../../../../../util/docTypeRuleDefaults';
import { formatIndividualName } from '../../../../../util/docTypeRuleEvaluator';
import { DOC_TYPE_SCOPE, splitDocTypesByScope, useDocTypes } from '../../../../../util/docTypeRuleMeta';
import { doGET } from '../../../../../util/HttpUtil';
import ButtonGroupNew from '../../BusinessLiteForms/components/ButtonGroupNew';
import ProofOfDocument from '../../BusinessLiteForms/components/ProofOfDocument';
import { BUSINESS_REVIEW_EDIT_PERMISSIONS, getBusinessReviewPermissions, getNextStep, getPrimaryIndividualIndex, WIZARD_NUM } from '../helper';

// Map EDD API ubo doc map into renderable structure, optionally unioning rule-provided doc types.
const mapDynamicUboDocs = (docMap = {}, individuals = [], reasons = {}, extraDocTypes = []) => {
    if (!docMap) return [];
    const individualsById = (individuals || []).reduce((acc, individual) => {
        if (individual?._id) {
            acc[individual._id] = individual;
        }
        return acc;
    }, {});

    return Object.entries(docMap)
        .map(([uboId, docTypes]) => {
            const normalizedDocTypes = Array.from(
                new Set([...(Array.isArray(docTypes) ? docTypes : []), ...(extraDocTypes || [])]),
            );
            if (!normalizedDocTypes.length) return null;
            const individual = individualsById[uboId];
            if (!individual) return null;
            return {
                uboId,
                docTypes: normalizedDocTypes,
                individual,
                displayName: formatIndividualName(individual),
                reason: reasons?.[uboId],
            };
        })
        .filter(Boolean);
};

// Renders document upload for Business Plus Periodic Review flows:
// - Fetch required documents from /api/business/edd-info (business + per-UBO).
// - Fetch doc-type-rule matches for this form and application data.
// - Render the unique union of EDD docs and rule docs (business + UBO) with validation.
const Documents = ({
    loading,
    asLabel,
    setHasError,
    data,
    handleUpdate,
    handlePrev,
    editId,
    applicationForm,
    step,
    wizard,
    hideButtons,
    editMode,
}) => {
    const { showError } = useContext(DialogContext);
    const [internalLoading, setInternalLoading] = useState(false);
    const [docRequirementsLoading, setDocRequirementsLoading] = useState(false);
    const [businessDocTypes, setBusinessDocTypes] = useState([]);
    const [dynamicUboDocMap, setDynamicUboDocMap] = useState(null);
    const [uboReasons, setUboReasons] = useState({});
    const [errors, setErrors] = useState([]);
    // Live doc type metadata (label/error/file types) pulled from /api/doc-type/grid
    const { docTypeMap, getMeta, loading: docTypesLoading, error: docTypesError } = useDocTypes();
    // Determine doc-type rule form mapping
    const docTypeFormType = DOC_TYPE_FORM_TYPES.BUSINESS_PLUS_PR;
    const docRuleApplicationData = useMemo(
        () => ({ ...data, applicationForm }),
        [applicationForm, data],
    );
    const {
        docTypes: ruleDocTypes,
        loading: docRuleLoading,
        error: docRuleError,
    } = useDocTypeRules({
        formType: docTypeFormType,
        applicationData: docRuleApplicationData,
    });
    const scopedRuleDocTypes = useMemo(
        () => splitDocTypesByScope(ruleDocTypes || [], docTypeMap),
        [docTypeMap, ruleDocTypes],
    );

    useEffect(() => {
        if (docRuleError) {
            showError(docRuleError?.message || "Failed to load document rules.");
        }
    }, [docRuleError, showError]);
    const ruleIndividualDocTypes = scopedRuleDocTypes.individual || [];

    useEffect(() => {
        if (docTypesError) {
            showError(docTypesError?.message || "Failed to load document types.");
        }
    }, [docTypesError, showError]);

    const fetchDocRequirements = useCallback(async () => {
        if (!editId) return;

        setDocRequirementsLoading(true);
        try {
            const response = await doGET(`/api/business/edd-info?id=${editId}`);
            const toCaptureDocs = response?.data?.toCaptureDocs;

            if (response?.status === 200 && toCaptureDocs) {
                const { BUSINESS: businessDocs = [], ...uboDocMap } = toCaptureDocs;
                // Always honor EDD-provided docs as baseline.
                setBusinessDocTypes(Array.isArray(businessDocs) ? businessDocs : []);
                setDynamicUboDocMap(uboDocMap);
                setUboReasons(response?.data?.uboReasonsForKYC || {});
            }
        } catch (error) {
            console.error(error);
            showError(error);
        } finally {
            setDocRequirementsLoading(false);
        }
    }, [editId, showError]);

    useEffect(() => {
        fetchDocRequirements();
    }, [fetchDocRequirements]);

    const reviewPermissions = useMemo(
        () => ({
            ...BUSINESS_REVIEW_EDIT_PERMISSIONS,
            ...(getBusinessReviewPermissions ? getBusinessReviewPermissions() : {}),
        }),
        [],
    );
    const allowedIndividuals = useMemo(() => {
        const individuals = Array.isArray(data?.individuals) ? data.individuals : [];
        if (reviewPermissions?.allowAdditionalIndividuals) return individuals;
        const primaryIdx = getPrimaryIndividualIndex(data);
        if (primaryIdx === null || primaryIdx === undefined || primaryIdx < 0) return [];
        return individuals[primaryIdx] ? [individuals[primaryIdx]] : [];
    }, [data, reviewPermissions]);

    // Business docs: union of EDD docs and rule docs (unique set).
    const businessDocTypesToRender = useMemo(() => {
        const eddDocs = Array.isArray(businessDocTypes) ? businessDocTypes : [];
        const ruleDocs = scopedRuleDocTypes.business || [];
        return Array.from(new Set([...(eddDocs || []), ...(ruleDocs || [])]));
    }, [businessDocTypes, scopedRuleDocTypes.business]);

    // UBO docs: union of EDD map entries and rule-matched docs.
    const finalUboDocRequirements = useMemo(() => {
        const ruleDocs = ruleIndividualDocTypes || [];
        // When EDD API provides per-UBO doc requirements (even if empty), treat it as authoritative.
        // This avoids showing document upload slots for every operator in Phase 2.
        if (dynamicUboDocMap !== null && dynamicUboDocMap !== undefined) {
            return mapDynamicUboDocs(dynamicUboDocMap, allowedIndividuals, uboReasons, ruleDocs);
        }

        if (ruleDocs.length && allowedIndividuals?.length) {
            return allowedIndividuals.map((individual) => ({
                uboId: individual?._id,
                individual,
                displayName: formatIndividualName(individual),
                docTypes: ruleDocs,
            }));
        }
        return [];
    }, [dynamicUboDocMap, allowedIndividuals, uboReasons, ruleIndividualDocTypes]);

    const checkIsDocumentUploaded = async ({ recordId, ubo_id, type }) => {
        try {
            const response = await doGET(
                `/api/application-doc/grid?rows=-1&form_id=${recordId}${ubo_id ? `&ubo_id=${ubo_id}` : ''}&type=${type}`,
            );
            if (response?.status === 200 && response?.data?.rows?.[0]?.attachment?.urls?.length) {
                return true;
            }
        } catch (error) {
            showError(error);
        }
        return false;
    };

    const validateBusinessDocuments = async () => {
        if (!businessDocTypesToRender?.length) {
            return { isValid: true, busErrors: [] };
        }

        const collectedErrors = [];
        let isValid = true;

        for (const docType of businessDocTypesToRender) {
            const meta = getMeta(docType, { scope: DOC_TYPE_SCOPE.BUSINESS });
            const isUploaded = await checkIsDocumentUploaded({ recordId: editId, type: meta.type });
            if (!isUploaded) {
                isValid = false;
                collectedErrors.push({ type: docType, error: meta.errorMessage });
            }
        }

        return { isValid, busErrors: collectedErrors };
    };

    const handleSubmit = async () => {
        setInternalLoading(true);
        setErrors([]);

        const aggregatedErrors = [];

        for (const entry of finalUboDocRequirements) {
            const { uboId, docTypes, individual, displayName } = entry;
            if (!docTypes?.length) continue;

            for (const docType of docTypes) {
                const meta = getMeta(docType, { scope: DOC_TYPE_SCOPE.INDIVIDUAL, individual, displayName });
                const isUploaded = await checkIsDocumentUploaded({ ubo_id: uboId, recordId: editId, type: meta.type });
                if (!isUploaded) {
                    aggregatedErrors.push({
                        error: meta.errorMessage,
                        type: docType,
                        ubo_id: uboId,
                    });
                }
            }
        }

        const { isValid: isBusinessValid, busErrors } = await validateBusinessDocuments();
        const combinedErrors = [...aggregatedErrors, ...busErrors];

        if (combinedErrors.length > 0 || !isBusinessValid) {
            setHasError(true);
            setErrors(combinedErrors);
            if (combinedErrors[0]?.error) {
                showError(combinedErrors[0]?.error);
            }
            setInternalLoading(false);
            return;
        }

        setHasError(false);
        await handleUpdate({ nextStep: getNextStep(wizard, applicationForm, data, editMode) });
        setInternalLoading(false);
    };

    const noDocumentsRequired = !businessDocTypesToRender?.length && !finalUboDocRequirements?.length;
    const isDocumentPageReady = !docRequirementsLoading && !docRuleLoading && !docTypesLoading;

    const renderBusinessDocument = (docType) => {
        const meta = getMeta(docType, { scope: DOC_TYPE_SCOPE.BUSINESS });

        return (
            <ProofOfDocument
                key={docType}
                recordId={editId}
                asLabel={asLabel}
                label={meta.label}
                field={meta.field}
                showCategory={meta.showCategory}
                type={meta.type}
                single={meta.single}
                supportedFiles={meta.supportedFiles}
                validateByAi={meta.validateByAi}
                error={errors.find((err) => err.type === docType)?.error}
            />
        );
    };

    const renderUboDocument = (entry, docType) => {
        const meta = getMeta(docType, {
            scope: DOC_TYPE_SCOPE.INDIVIDUAL,
            individual: entry.individual,
            displayName: entry.displayName,
        });

        return (
            <ProofOfDocument
                key={`${entry.uboId}-${docType}`}
                recordId={editId}
                asLabel={asLabel}
                label={meta.label}
                field={meta.field}
                showCategory={meta.showCategory}
                type={meta.type}
                ubo_id={entry.uboId}
                single={meta.single}
                supportedFiles={meta.supportedFiles}
                validateByAi={meta.validateByAi}
                error={errors.find((err) => err.ubo_id === entry.uboId && err.type === docType)?.error}
            />
        );
    };

    return (
        <div>
            <h2 className='pt-3'>Document Upload Center</h2>
            <p style={{ fontSize: 12, fontStyle: 'italic' }}>
                Please upload all of the required documents for your application. If you do not have them ready, you can return later to
                complete the application.
            </p>

            {docRequirementsLoading || docRuleLoading || docTypesLoading ? (
                <div style={{ width: '100%', height: 'calc(100vh - 340px)' }} className='d-flex justify-content-center align-items-center'>
                    <span className='text-muted'>Fetching document requirements…</span>
                </div>
            ) : (
                <>
                    <div className='py-2'>Important things to note:</div>
                    <ul>
                        <li>All documents must be in English or accompanied by an official certified translated copy.</li>
                        <li>Register of directors and shareholders must be certified by CPA/Lawyer/Notary.</li>
                        <li>Please read the help text below the upload box for each document and its requirements.</li>
                    </ul>

                    {noDocumentsRequired && (
                        <Card className='p-3 mb-3'>
                            <div className='fw-semibold'>No documents required at this stage.</div>
                            <div className='text-muted small'>We will notify you if additional verification materials are required later.</div>
                        </Card>
                    )}

                    {finalUboDocRequirements.map((entry, idx) => (
                        <div key={entry.uboId || idx}>
                            {entry.docTypes.map((docType) => renderUboDocument(entry, docType))}
                        </div>
                    ))}

                    {businessDocTypesToRender.map((docType) => renderBusinessDocument(docType))}

                    {/* <p className='fs-6 py-2'>
                        Once you submit your application, each of the Officers, Authorized Users and Shareholders with 25% or more equity will be sent a text message to
                        verify their identity. Please be sure each person completes the identity verification within 48 hours or the application will be placed on hold.
                    </p> */}

                    <div className='mt-4'>
                        <ButtonGroupNew
                            onNextClick={handleSubmit}
                            onBackClick={handlePrev}
                            showPageNum={false}
                            pageNum={WIZARD_NUM.Documents}
                            step={WIZARD_NUM.Documents}
                            isDisabled={asLabel}
                            isReadyForReviewAction={isDocumentPageReady}
                            reviewPendingReason="Please wait while document requirements finish loading."
                            asLabel={asLabel}
                            loading={internalLoading || loading || docRequirementsLoading || docRuleLoading || docTypesLoading}
                            hideSubmitButton={hideButtons}
                        />
                    </div>
                </>
            )}
        </div>
    );
};

export default Documents;
